import { OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { AuthService } from '../../../../service/autenticacao/auth.service';
import { LaiFormFactory } from '../../../../service/lai/lai-form-factory.service';

export abstract class LaiBaseComponent implements OnInit {
  form: FormGroup;
  formSubmitted = false;
  tematicasDisponiveis = [
    {
      codigo: 'CONTRATOS_CONVENIOS',
      descricao: 'Como acessar informações ou onde encontrá-las',
      ajuda: 'Orientações sobre o passo a passo para conseguir uma informação ou indicação de qual lugar/site ela está disponível. (Ref: Art. 7º, I)'
    },
    {
      codigo: 'LICITACOES',
      descricao: 'Documentos ou registros oficiais',
      ajuda: 'Documentos produzidos ou guardados por este órgão, estejam eles em arquivos atuais ou antigos. (Ref: Art. 7º, II)'
    },
    {
      codigo: 'ORCAMENTO_FINANCAS',
      descricao: 'Informações de parceiros ou empresas privadas com vínculo público',
      ajuda: 'Dados gerados por pessoas ou empresas que prestam (ou prestaram) serviços para o governo ou possuem contratos conosco. (Ref: Art. 7º, III)'
    },
    {
      codigo: 'RECURSOS_HUMANOS',
      descricao: 'Dados originais e atualizados (Informação Primária)',
      ajuda: 'Acesso aos dados brutos, na forma original e sem alterações. (Ref: Art. 7º, IV)'
    },
    {
      codigo: 'PROCESSOS_ADMINISTRATIVOS',
      descricao: 'Como o órgão funciona e o que ele faz',
      ajuda: 'Informações sobre a nossa organização, os serviços que oferecemos, nossas políticas e como trabalhamos. (Ref. Art. 7º, V)'
    },
    {
      codigo: 'ESTATISTICAS',
      descricao: 'Uso do dinheiro público, licitações e contratos',
      ajuda: 'Detalhes sobre gastos, compras do governo (licitações), contratos assinados e como o patrimônio público é administrado. (Ref: Art. 7º, VI)'
    },
    {
      codigo: 'NORMAS_ATOS',
      descricao: 'Resultados de programas, projetos e metas',
      ajuda: 'Informações sobre o andamento de projetos públicos, se as metas foram batidas e quais foram os resultados alcançados. (Ref: Art. 7º, VII, \'a\')'
    },
    {
      codigo: 'SERVICOS_ATENDIMENTO',
      descricao: 'Fiscalizações, auditorias e contas públicas',
      ajuda: 'Resultados de inspeções e auditorias realizadas por órgãos de controle, além das prestações de contas de anos atuais ou passados. (Ref: Art. 7º, VII, \'b\')'
    },
    { codigo: 'OUTROS', descricao: 'Outros' }
  ];

  constructor(
    protected authService: AuthService,
    protected formFactory: LaiFormFactory
  ) {
    this.form = this.formFactory.criarFormularioBase(this.authService.usuarioDTO || {});
  }

  ngOnInit() {
    this.preencherDadosUsuarioLogado();
    this.monitorarMudancas();
    this.atualizarEstadoRepresentanteLegal();
    this.atualizarEstadoFormaRetorno();
  }

  get representanteLegalGroup(): FormGroup {
    return this.form.get('representanteLegal') as FormGroup;
  }

  get enderecoGroup(): FormGroup {
    return this.form.get('endereco') as FormGroup;
  }

  get tematicasArray(): FormArray {
    return this.form.get('tematicas') as FormArray;
  }

  get anexosArray(): FormArray {
    return this.form.get('anexos') as FormArray;
  }

  get anexosRepresentanteLegalArray(): FormArray {
    return this.form.get('anexosRepresentanteLegal') as FormArray;
  }

  get isRepresentanteLegal(): boolean {
    return this.form.get('titularidade').value === 'REPRESENTANTE_LEGAL';
  }

  get isRetornoCarta(): boolean {
    return this.form.get('formaRetorno').value === 'CARTA';
  }

  protected preencherDadosUsuarioLogado() {
    const usuario: any = this.authService.usuarioDTO || {};
    this.form.patchValue({
      nomeUsuarioLogado: usuario.nome || '',
      emailUsuarioLogado: usuario.email || '',
      cpfUsuarioLogado: usuario.cpfcnpj || '',
      dataNascimentoUsuario: usuario.dataNascimento || '',
      prioridadePessoaIdosa: usuario.prioridadePessoaIdosa === true,
      username: usuario.username || '',
      emailResposta: usuario.email || ''
    }, { emitEvent: false });
  }

  protected monitorarMudancas() {
    this.form.get('titularidade').valueChanges.subscribe(() => this.atualizarEstadoRepresentanteLegal());
    this.form.get('formaRetorno').valueChanges.subscribe(() => this.atualizarEstadoFormaRetorno());
    this.tematicasArray.valueChanges.subscribe(() => this.atualizarEstadoOutrosAssunto());
    this.representanteLegalGroup.get('tipoPessoa').valueChanges.subscribe(() => {
      this.representanteLegalGroup.get('cpfCnpj').updateValueAndValidity();
    });
  }

  protected atualizarEstadoRepresentanteLegal() {
    const controles = this.representanteLegalGroup.controls;
    const ativo = this.isRepresentanteLegal;

    Object.keys(controles).forEach(campo => {
      controles[campo].clearValidators();
    });

    if (ativo) {
      controles.tipoPessoa.setValidators([Validators.required]);
      controles.nomeRazaoSocial.setValidators([Validators.required]);
      controles.cpfCnpj.setValidators([Validators.required, this.cpfCnpjRepresentanteValidator.bind(this)]);
      controles.dataNascimento.setValidators([Validators.required]);
      controles.email.setValidators([Validators.required, Validators.email]);
      controles.telefone.setValidators([Validators.required]);
      this.anexosRepresentanteLegalArray.setValidators([Validators.required]);
    } else {
      this.representanteLegalGroup.reset({ tipoPessoa: 'FISICA' }, { emitEvent: false });
      this.anexosRepresentanteLegalArray.clear();
      this.anexosRepresentanteLegalArray.clearValidators();
    }

    Object.keys(controles).forEach(campo => {
      controles[campo].updateValueAndValidity({ emitEvent: false });
    });
    this.anexosRepresentanteLegalArray.updateValueAndValidity({ emitEvent: false });
  }

  protected atualizarEstadoFormaRetorno() {
    const emailResposta = this.form.get('emailResposta');
    const endereco = this.enderecoGroup.controls;

    emailResposta.clearValidators();
    Object.keys(endereco).forEach(campo => endereco[campo].clearValidators());

    if (this.isRetornoCarta) {
      endereco.cep.setValidators([Validators.required]);
      endereco.logradouro.setValidators([Validators.required]);
      endereco.numero.setValidators([Validators.required]);
      endereco.complemento.setValidators([Validators.required]);
      endereco.bairro.setValidators([Validators.required]);
      endereco.cidade.setValidators([Validators.required]);
      endereco.uf.setValidators([Validators.required]);
    } else {
      emailResposta.setValidators([Validators.required, Validators.email]);
      this.enderecoGroup.reset({}, { emitEvent: false });
    }

    emailResposta.updateValueAndValidity({ emitEvent: false });
    Object.keys(endereco).forEach(campo => endereco[campo].updateValueAndValidity({ emitEvent: false }));
  }

  protected atualizarEstadoOutrosAssunto() {
    const outrosAssunto = this.form.get('outrosAssunto');
    outrosAssunto.clearValidators();
    if (this.tematicaSelecionada('OUTROS')) {
      outrosAssunto.setValidators([Validators.required, Validators.maxLength(1000)]);
    }
    outrosAssunto.updateValueAndValidity({ emitEvent: false });
  }

  tematicaSelecionada(codigo: string): boolean {
    return this.tematicasArray.value && this.tematicasArray.value.some(item => item.codigo === codigo);
  }

  onTematicaChange(tematica, event: Event) {
    const input = event.target as HTMLInputElement;
    this.toggleTematica(tematica, input.checked);
  }

  toggleTematica(tematica, checked: boolean) {
    const index = this.tematicasArray.value.findIndex(item => item.codigo === tematica.codigo);

    if (checked) {
      this.tematicasArray.clear();
      this.tematicasArray.push(new FormGroup({
        codigo: new FormControl(tematica.codigo),
        descricao: new FormControl(tematica.descricao)
      }));
    }

    if (!checked && index >= 0) {
      this.tematicasArray.removeAt(index);
    }

    this.tematicasArray.markAsTouched();
    this.tematicasArray.updateValueAndValidity();
    this.atualizarEstadoOutrosAssunto();
  }

  campoInvalido(caminho: string): boolean {
    const controle = this.form.get(caminho);
    return !!controle && controle.invalid && (controle.touched || controle.dirty || this.formSubmitted);
  }

  controleInvalido(controle: AbstractControl): boolean {
    return !!controle && controle.invalid && (controle.touched || controle.dirty || this.formSubmitted);
  }

  nomeArquivoAnexo(controle: AbstractControl): string {
    const nomeArquivo = controle && controle.get('nomeArquivo')
      ? controle.get('nomeArquivo').value
      : '';

    return String(nomeArquivo || '').split('/type=')[0];
  }

  removerAnexo(anexos: FormArray, index: number) {
    anexos.removeAt(index);
    anexos.markAsDirty();
    anexos.markAsTouched();
    anexos.updateValueAndValidity();
  }

  protected criarPayload() {
    const raw = this.form.getRawValue();
    const selecionouOutros = raw.tematicas && raw.tematicas.some(item => item.codigo === 'OUTROS');

    return {
      id: raw.id,
      nomeUsuarioLogado: this.normalizarTexto(raw.nomeUsuarioLogado),
      emailUsuarioLogado: this.normalizarTexto(raw.emailUsuarioLogado),
      cpfUsuarioLogado: this.normalizarTexto(raw.cpfUsuarioLogado),
      dataNascimentoUsuario: raw.dataNascimentoUsuario,
      prioridadePessoaIdosa: raw.prioridadePessoaIdosa === true,
      username: this.normalizarTexto(raw.username),
      titularidade: raw.titularidade,
      representanteLegal: raw.titularidade === 'REPRESENTANTE_LEGAL' ? this.mapearRepresentanteLegal(raw.representanteLegal) : null,
      tematicas: raw.tematicas,
      outrosAssunto: selecionouOutros ? this.normalizarTexto(raw.outrosAssunto) : null,
      descricaoDetalhada: this.normalizarTexto(raw.descricaoDetalhada),
      formaRetorno: raw.formaRetorno,
      emailResposta: raw.formaRetorno === 'EMAIL' ? this.normalizarTexto(raw.emailResposta) : null,
      endereco: raw.formaRetorno === 'CARTA' ? this.mapearEndereco(raw.endereco) : null,
      anexos: this.mapearAnexos(raw.anexos),
      anexosRepresentanteLegal: raw.titularidade === 'REPRESENTANTE_LEGAL' ? this.mapearAnexos(raw.anexosRepresentanteLegal) : [],
      unidadeDestinoId: 100002839,
      unidadeDestinoSigla: 'OUVIDORIA'
    };
  }

  private mapearRepresentanteLegal(representante) {
    if (!representante) {
      return null;
    }

    return {
      tipoPessoa: representante.tipoPessoa,
      nomeRazaoSocial: this.normalizarTexto(representante.nomeRazaoSocial),
      cpfCnpj: this.normalizarTexto(representante.cpfCnpj),
      dataNascimento: representante.dataNascimento,
      email: this.normalizarTexto(representante.email),
      telefone: this.normalizarTexto(representante.telefone)
    };
  }

  private mapearEndereco(endereco) {
    if (!endereco) {
      return null;
    }

    return {
      cep: this.normalizarTexto(endereco.cep),
      logradouro: this.normalizarTexto(endereco.logradouro),
      numero: this.normalizarTexto(endereco.numero),
      complemento: this.normalizarTexto(endereco.complemento),
      bairro: this.normalizarTexto(endereco.bairro),
      cidade: this.normalizarTexto(endereco.cidade),
      uf: this.normalizarTexto(endereco.uf)
    };
  }

  private mapearAnexos(anexos) {
    if (!anexos || !anexos.length) {
      return [];
    }

    return anexos
      .filter(anexo => anexo && anexo.nomeArquivo && anexo.conteudo)
      .map(anexo => ({
        nomeArquivo: anexo.nomeArquivo,
        conteudo: anexo.conteudo
      }));
  }

  private normalizarTexto(valor): string {
    return valor === null || valor === undefined ? null : String(valor).trim();
  }

  private cpfCnpjRepresentanteValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const tipoPessoa = this.representanteLegalGroup.get('tipoPessoa').value;
    const digits = String(control.value).replace(/\D/g, '');

    if (tipoPessoa === 'JURIDICA') {
      return this.cnpjValido(digits) ? null : { cnpjInvalido: true };
    }

    return this.cpfValido(digits) ? null : { cpfInvalido: true };
  }

  private cpfValido(cpf: string): boolean {
    if (!cpf || cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
      return false;
    }
    const d1 = this.calcularCpfDigito(cpf, 9);
    const d2 = this.calcularCpfDigito(cpf, 10);
    return cpf.charAt(9) === String(d1) && cpf.charAt(10) === String(d2);
  }

  private calcularCpfDigito(cpf: string, tamanho: number): number {
    let soma = 0;
    for (let i = 0; i < tamanho; i++) {
      soma += Number(cpf.charAt(i)) * (tamanho + 1 - i);
    }
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  }

  private cnpjValido(cnpj: string): boolean {
    if (!cnpj || cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) {
      return false;
    }
    const d1 = this.calcularCnpjDigito(cnpj, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
    const d2 = this.calcularCnpjDigito(cnpj, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
    return cnpj.charAt(12) === String(d1) && cnpj.charAt(13) === String(d2);
  }

  private calcularCnpjDigito(cnpj: string, pesos: number[]): number {
    let soma = 0;
    for (let i = 0; i < pesos.length; i++) {
      soma += Number(cnpj.charAt(i)) * pesos[i];
    }
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  }
}
